import {createContext, useContext, useState, useEffect, FC, ReactNode} from 'react'
import {AdminUser, checkAdminAuth, adminLogout as apiAdminLogout, clearAdminToken, getAdminToken, saveAdminToken} from './_requests'

interface AdminAuthContextType {
  admin: AdminUser | null
  loading: boolean
  setAdmin: (admin: AdminUser | null) => void
  logout: () => Promise<void>
  refreshAuth: () => Promise<void>
}

const AdminAuthContext = createContext<AdminAuthContextType | null>(null)

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext)
  if (!context) {
    throw new Error('useAdminAuth must be used within AdminAuthProvider')
  }
  return context
}

interface AdminAuthProviderProps {
  children: ReactNode
}

export const AdminAuthProvider: FC<AdminAuthProviderProps> = ({children}) => {
  const [admin, setAdmin] = useState<AdminUser | null>(null)
  const [loading, setLoading] = useState(true)

  // Admin sessions never expire on their own. Only an explicit "loggedIn: false" from the
  // server logs out; network errors / 5xx / Cloudflare 52x during restarts keep the session
  // and retry, instead of bouncing the admin to the login screen.
  const refreshAuth = async (attempt = 0): Promise<void> => {
    try {
      const response = await checkAdminAuth()
      const data: any = response.data
      if (data.loggedIn && data.admin) {
        if (data.token) saveAdminToken(data.token)
        setAdmin(data.admin)
      } else {
        setAdmin(null)
      }
      setLoading(false)
    } catch (error) {
      console.error('Admin auth check failed (will retry):', error)
      if (getAdminToken() && attempt < 20) {
        setTimeout(() => refreshAuth(attempt + 1), Math.min(30000, 1000 * 2 ** attempt))
        return
      }
      setLoading(false)
    }
  }

  useEffect(() => {
    refreshAuth()
  }, [])

  const logout = async () => {
    setAdmin(null)
    clearAdminToken()
    try {
      await apiAdminLogout()
    } catch (error) {
      console.error('Logout error:', error)
    }
  }

  return (
    <AdminAuthContext.Provider value={{admin, loading, setAdmin, logout, refreshAuth}}>
      {children}
    </AdminAuthContext.Provider>
  )
}
